import { useEffect } from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Check,
  MessageSquare,
  Users,
  Sparkles,
  ShieldCheck,
  Workflow,
  PhoneCall,
  BarChart3,
  Plug,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { LeadForm } from "@/components/forms/LeadForm";
import { KENTRO } from "@/lib/constants";
import { trackEvent, trackMetaPixel, withUtm } from "@/lib/analytics";

export const CHATGPT_FAQS: { q: string; a: string }[] = [
  {
    q: "A Kentro é um CRM ou uma plataforma de atendimento?",
    a: "Ela reúne atendimento omnichannel, CRM e assistentes de IA. A proposta é conectar a conversa com a organização das oportunidades e das próximas ações da equipe.",
  },
  {
    q: "Consigo trabalhar com vários atendentes?",
    a: "A plataforma organiza usuários, filas e responsáveis pelo atendimento. A quantidade de pessoas e canais deve ser definida na configuração contratada.",
  },
  {
    q: "A IA pode usar as informações da minha empresa?",
    a: "Sim. A configuração utiliza informações e regras do negócio. O conteúdo deve ser revisado e os fluxos testados antes da operação com clientes.",
  },
  {
    q: "A IA pode encaminhar para uma pessoa?",
    a: "O fluxo pode prever transferência para atendimento humano. Na implantação, devem ser definidos os critérios, responsáveis e informações que precisam acompanhar esse encaminhamento.",
  },
  {
    q: "Posso usar meu número atual?",
    a: "A possibilidade depende dos requisitos do número e da configuração da conta. A equipe avalia as condições de migração antes da ativação.",
  },
  {
    q: "A Kentro integra com meu ERP?",
    a: "É necessário verificar o sistema e as ações desejadas. Há recursos de integração, mas disponibilidade, desenvolvimento e custos dependem do escopo.",
  },
  {
    q: "A Kentro envia SMS?",
    a: "Não oferece disparo nativo de SMS. Uma necessidade desse tipo pode ser avaliada por integração com um provedor externo.",
  },
  {
    q: "A Kentro funciona fora do horário comercial?",
    a: "Fluxos automatizados e assistentes de IA podem ser configurados para atuar fora do expediente. O atendimento humano segue os horários da equipe responsável.",
  },
  {
    q: "Quanto custa?",
    a: "Solicite uma proposta com usuários, canais, IA e integrações necessários. Confira os itens incluídos e eventuais custos de implantação, consumo e terceiros.",
  },
  {
    q: "Em quanto tempo posso começar?",
    a: "O prazo deve ser definido após avaliar os canais, a configuração da IA e as integrações. Projetos com dependências externas podem exigir etapas adicionais.",
  },
  {
    q: "Preciso substituir meu sistema de gestão?",
    a: "O projeto pode conectar atendimento e sistemas já utilizados, conforme a viabilidade das integrações. O que será mantido ou substituído deve ser definido no diagnóstico.",
  },
  {
    q: "Como avaliar se a Kentro atende minha operação?",
    a: "Leve para a demonstração uma situação real: um contato que precisa ser qualificado, um atendimento recorrente ou uma oportunidade que precisa de acompanhamento.",
  },
];

const UTM = {
  utm_source: "chatgpt",
  utm_medium: "paid",
  utm_campaign: "lp_chatgpt",
};

const WHATSAPP_HREF = withUtm(
  "https://api.whatsapp.com/send/?phone=553832208706&text=" +
    encodeURIComponent("Olá! Vim pelo ChatGPT e quero conhecer a Kentro.") +
    "&type=phone_number&app_absent=0",
  UTM,
);

const IA_HREF = "/ia?utm_source=chatgpt&utm_medium=paid&utm_campaign=lp_chatgpt";

const ANCHORS = [
  { id: "funcionamento", label: "Funcionamento" },
  { id: "segmentos", label: "Segmentos" },
  { id: "integracoes", label: "Integrações" },
  { id: "clientes", label: "Clientes" },
  { id: "duvidas", label: "Dúvidas" },
];

const PROBLEMS = [
  {
    situation: "Mensagens sem responsável",
    answer: "Filas e distribuição de atendimentos",
    evidence: "Conversa atribuída a uma pessoa.",
  },
  {
    situation: "Orçamentos esquecidos",
    answer: "Oportunidades e tarefas de acompanhamento",
    evidence: "Card no funil com próxima ação.",
  },
  {
    situation: "Dúvidas repetitivas",
    answer: "IA configurada com informações da empresa",
    evidence: "Pergunta respondida com base aprovada.",
  },
  {
    situation: "Cliente repete informações",
    answer: "Histórico disponível no atendimento",
    evidence: "Transferência com contexto.",
  },
  {
    situation: "Gestão sem visibilidade",
    answer: "Relatórios da operação",
    evidence: "Indicadores com período e definição.",
  },
];

const CAPABILITIES = [
  {
    icon: MessageSquare,
    title: "Atendimento omnichannel",
    text: "Organize conversas de canais diferentes e direcione cada demanda para a equipe responsável.",
    to: "/omnichannel",
    linkLabel: "Ver atendimento omnichannel",
  },
  {
    icon: Users,
    title: "CRM integrado",
    text: "Acompanhe oportunidades, etapas e próximas ações junto ao relacionamento com o cliente.",
    to: "/funcionalidades",
    linkLabel: "Ver módulo de CRM",
  },
  {
    icon: Sparkles,
    title: "Assistentes de IA",
    text: "Configure informações e regras para responder dúvidas, qualificar contatos e encaminhar atendimentos.",
    to: "/ia",
    linkLabel: "Conhecer a IA",
  },
  {
    icon: Workflow,
    title: "Automação",
    text: "Estruture rotinas de distribuição e acompanhamento conforme os recursos contratados.",
    to: "/funcionalidades",
    linkLabel: "Ver módulos de automação",
  },
  {
    icon: PhoneCall,
    title: "Voz e PABX",
    text: "Conecte o atendimento por voz à organização da operação, conforme a configuração contratada.",
    to: "/funcionalidades",
    linkLabel: "Ver módulo de voz",
  },
  {
    icon: BarChart3,
    title: "Relatórios",
    text: "Acompanhe indicadores disponíveis de atendimento e vendas para identificar gargalos.",
    to: "/funcionalidades",
    linkLabel: "Ver módulo de relatórios",
  },
] as const;

const SEGMENT_CARDS = [
  {
    name: "Atendimento geral",
    scenario:
      "Organizar filas, responsáveis e continuidade entre atendimento e vendas.",
    condition: "Depende da configuração das equipes e canais.",
    slug: null,
    clinicas: false,
  },
  {
    name: "Provedores de internet",
    scenario:
      "Separar comercial, suporte e financeiro; registrar a solicitação antes de encaminhar.",
    condition:
      "Consultas de contrato e cobrança dependem dos sistemas conectados.",
    slug: "provedores-de-internet",
    clinicas: false,
  },
  {
    name: "Farmácias e manipulação",
    scenario:
      "Receber pedidos e solicitações de orçamento; encaminhar dúvidas ao profissional responsável.",
    condition:
      "Estoque, preços e etapas da manipulação dependem da fonte conectada.",
    slug: "farmacias",
    clinicas: false,
  },
  {
    name: "Clínicas médicas, odontológicas e estéticas",
    scenario:
      "Responder dúvidas administrativas e organizar solicitações de agendamento.",
    condition:
      "Confirmação de horário depende de agenda e integração disponíveis.",
    slug: null,
    clinicas: true,
  },
  {
    name: "Escolas, faculdades e cursos",
    scenario:
      "Esclarecer informações institucionais e acompanhar interessados até o próximo passo da matrícula.",
    condition:
      "Matrícula efetiva depende do processo e dos sistemas da instituição.",
    slug: "escolas-e-faculdades",
    clinicas: false,
  },
] as const;

const STEPS = [
  "Entender a operação: canais usados hoje, volume de conversas e quem atende.",
  "Definir canais e responsáveis: filas, equipes e critérios de distribuição.",
  "Configurar o escopo: informações da IA, etapas do funil e integrações viáveis.",
  "Testar: revisar respostas da IA e o caminho da conversa antes do público.",
  "Orientar a equipe: uso da caixa de entrada, do funil e das transferências.",
  "Iniciar e acompanhar: ajustar regras e indicadores conforme o uso real.",
];

export function ChatGptLandingPage() {
  useEffect(() => {
    try {
      sessionStorage.setItem("kentro_campaign_source", "chatgpt");
      sessionStorage.setItem("kentro_campaign_ts", String(Date.now()));
    } catch {
      /* noop */
    }
    trackEvent("campaign_lp_view", { source: "chatgpt" });
  }, []);

  const click = (cta: string) => trackEvent("campaign_lp_click", { source: "chatgpt", cta });

  return (
    <main className="relative">
      {/* 1 — Abertura */}
      <section className="gradient-hero relative overflow-hidden pt-28 pb-16 md:pt-36 md:pb-20">
        <div className="circuit-pattern absolute inset-0 opacity-30" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <span className="glass-card inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary-glow">
              <Sparkles className="size-3.5" /> WhatsApp Oficial • Atendimento omnichannel • CRM • IA
            </span>
            <h1
              className="mt-5 text-4xl font-bold tracking-tight md:text-5xl"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Transforme conversas em vendas com{" "}
              <span className="gradient-text">atendimento por IA e CRM integrado</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg text-muted-foreground">
              A Kentro reúne seus canais de atendimento, organiza oportunidades no CRM e usa IA para
              responder dúvidas e qualificar contatos. Sua equipe acompanha cada conversa e assume o
              atendimento com o contexto disponível.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                className="gradient-primary text-white shadow-glow hover:opacity-95"
              >
                <a href="#demonstracao" onClick={() => click("demo_hero")}>
                  Solicitar demonstração <ArrowRight />
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/15 bg-white/5 text-foreground hover:bg-white/10"
              >
                <a href={IA_HREF} onClick={() => click("testar_ia_hero")}>
                  Experimentar a IA do meu segmento
                </a>
              </Button>
            </div>

            <ul className="mt-8 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
              {[
                "API Oficial do WhatsApp",
                "Instagram, e-mail, webchat e voz",
                "Funil de oportunidades no CRM",
                "IA configurada com suas regras",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="mt-0.5 size-4 shrink-0 text-[var(--mint)]" />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Ilustração de interface — identificada como demonstração */}
          <div className="glass-card rounded-3xl p-5 shadow-elegant">
            <div className="flex items-center justify-between border-b border-white/5 pb-3 text-xs text-muted-foreground">
              <span>Central de atendimento (exemplo ilustrativo)</span>
              <span className="rounded-full bg-primary/15 px-2 py-0.5 text-primary-glow">
                Demonstração
              </span>
            </div>
            <div className="mt-4 space-y-3 text-sm">
              <Bubble side="user">
                Tenho uma clínica e recebo muitas mensagens fora do horário.
              </Bubble>
              <Bubble side="ai">
                Posso te ajudar a entender como organizar isso. Qual dificuldade mais atrapalha sua
                recepção hoje?
              </Bubble>
            </div>
            <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-xs">
              <div className="font-semibold text-foreground">Oportunidade no funil</div>
              <dl className="mt-2 grid gap-1 text-muted-foreground">
                <div className="flex justify-between gap-3">
                  <dt>Necessidade registrada</dt>
                  <dd className="text-foreground">Mensagens fora do horário</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt>Etapa</dt>
                  <dd className="text-foreground">Qualificação</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt>Responsável</dt>
                  <dd className="text-foreground">Equipe comercial</dd>
                </div>
              </dl>
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">
              Dados fictícios usados apenas para ilustrar a tela.
            </p>
          </div>
        </div>
      </section>

      {/* Menu por âncoras */}
      <nav
        aria-label="Seções desta página"
        className="sticky top-16 z-30 border-y border-white/10 bg-background/85 backdrop-blur"
      >
        <ul className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 py-2 text-sm sm:px-6 lg:px-8">
          {ANCHORS.map((a) => (
            <li key={a.id}>
              <a
                href={`#${a.id}`}
                className="inline-block rounded-full px-3 py-1.5 text-muted-foreground transition hover:bg-white/10 hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/50"
              >
                {a.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {/* 2 — Definição objetiva */}
      <Light>
        <div className="mx-auto max-w-5xl">
          <h2 className="text-3xl font-bold md:text-4xl" style={{ fontFamily: "var(--font-display)" }}>
            O que é a Kentro e para quem ela serve?
          </h2>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
            A Kentro é uma plataforma de atendimento omnichannel com CRM integrado e assistentes de
            inteligência artificial. Ela ajuda empresas a centralizar conversas, distribuir
            atendimentos, acompanhar oportunidades e automatizar etapas do relacionamento com
            clientes. É indicada para operações de atendimento, vendas e suporte que precisam
            organizar canais e dar continuidade a cada contato.
          </p>

          <dl className="mt-8 grid gap-4 sm:grid-cols-2">
            <Fact term="Categoria" desc="Plataforma de atendimento omnichannel com CRM e IA." />
            <Fact
              term="Canais disponíveis"
              desc="WhatsApp (API Oficial), Instagram, e-mail, webchat e voz, conforme a configuração contratada."
            />
            <Fact
              term="Recursos principais"
              desc="Caixa de entrada compartilhada, filas e responsáveis, funil de oportunidades, assistentes de IA, automações, relatórios."
            />
            <Fact
              term="Modalidade de implantação"
              desc="Plataforma em nuvem, com configuração assistida pela equipe Kentro."
            />
          </dl>

          <Link
            to="/funcionalidades"
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-primary-glow hover:underline"
            onClick={() => click("funcionalidades_ficha")}
          >
            Ver detalhes de cada módulo em funcionalidades <ChevronRight className="size-4" />
          </Link>
        </div>
      </Light>

      {/* 3 — Problemas reconhecíveis */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold md:text-4xl" style={{ fontFamily: "var(--font-display)" }}>
            Onde sua operação perde tempo e oportunidades?
          </h2>
          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {PROBLEMS.map((p) => (
              <div key={p.situation} className="glass-card rounded-2xl p-5">
                <h3 className="text-base font-semibold">{p.situation}</h3>
                <p className="mt-2 text-sm text-foreground/90">{p.answer}</p>
                <p className="mt-2 text-xs text-muted-foreground">{p.evidence}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — Funcionamento na prática */}
      <Light id="funcionamento">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-3xl font-bold md:text-4xl" style={{ fontFamily: "var(--font-display)" }}>
            Do primeiro contato ao próximo passo da venda
          </h2>
          <p className="mt-4 max-w-3xl text-muted-foreground">
            Uma pessoa pergunta por uma solução; a IA identifica a necessidade conforme as suas
            regras; a oportunidade fica organizada no CRM; a equipe continua a conversa; o
            acompanhamento permanece registrado.
          </p>

          <div className="mt-8 grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-primary-glow">
                Roteiro ilustrativo (demonstração)
              </div>
              <div className="mt-4 space-y-3 text-sm">
                <Bubble side="user">
                  Tenho uma clínica e recebo muitas mensagens fora do horário.
                </Bubble>
                <Bubble side="ai">
                  Posso te ajudar a entender como organizar isso. Qual dificuldade mais atrapalha sua
                  recepção hoje?
                </Bubble>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Recorte curto de conversa. Não representa a cadência completa de qualificação nem
                garante respostas idênticas.
              </p>
            </div>
            <ol className="space-y-3">
              {[
                "A conversa chega no canal escolhido e entra na fila correta.",
                "A IA registra a necessidade informada e segue as regras definidas.",
                "A oportunidade aparece no funil com etapa e próxima ação.",
                "Um atendente recebe o contexto e conduz a apresentação.",
                "O acompanhamento fica registrado no histórico do contato.",
              ].map((s, i) => (
                <li key={s} className="glass-card flex gap-3 rounded-xl p-4 text-sm">
                  <span className="gradient-primary inline-flex size-7 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white">
                    {i + 1}
                  </span>
                  {s}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </Light>

      {/* 5 — Capacidades */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold md:text-4xl" style={{ fontFamily: "var(--font-display)" }}>
            Atendimento, vendas e gestão no mesmo fluxo
          </h2>
          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.title} className="glass-card flex flex-col rounded-2xl p-5">
                  <span className="gradient-primary inline-flex size-10 items-center justify-center rounded-xl text-white shadow-glow">
                    <Icon className="size-5" />
                  </span>
                  <h3 className="mt-4 text-lg font-semibold">{c.title}</h3>
                  <p className="mt-2 flex-1 text-sm text-muted-foreground">{c.text}</p>
                  <Link
                    to={c.to}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-glow hover:underline"
                    onClick={() => click(`capacidade_${c.title}`)}
                  >
                    {c.linkLabel} <ChevronRight className="size-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6 — Segmentos */}
      <Light id="segmentos">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold md:text-4xl" style={{ fontFamily: "var(--font-display)" }}>
            Veja como a Kentro pode funcionar no seu negócio
          </h2>
          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {SEGMENT_CARDS.map((s) => (
              <div key={s.name} className="glass-card flex flex-col rounded-2xl p-5">
                <h3 className="text-base font-semibold">{s.name}</h3>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{s.scenario}</p>
                <p className="mt-3 text-xs text-muted-foreground">
                  <strong className="text-foreground/80">Condição:</strong> {s.condition}
                </p>
                {s.clinicas ? (
                  <Link
                    to="/clinicas"
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-glow hover:underline"
                    onClick={() => click(`segmento_${s.name}`)}
                  >
                    Ver página do segmento <ChevronRight className="size-4" />
                  </Link>
                ) : s.slug ? (
                  <Link
                    to="/segmentos/$slug"
                    params={{ slug: s.slug }}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-glow hover:underline"
                    onClick={() => click(`segmento_${s.name}`)}
                  >
                    Ver página do segmento <ChevronRight className="size-4" />
                  </Link>
                ) : (
                  <a
                    href="#demonstracao"
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-glow hover:underline"
                    onClick={() => click("segmento_geral")}
                  >
                    Solicitar demonstração <ChevronRight className="size-4" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </Light>

      {/* 7 — IA com limites claros */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold md:text-4xl" style={{ fontFamily: "var(--font-display)" }}>
            Uma IA configurada para o jeito que sua empresa atende
          </h2>
          <p className="mt-4 max-w-3xl text-muted-foreground">
            Configure a IA com informações sobre sua empresa, produtos, processos e perguntas
            frequentes. Defina o que ela pode responder, quais dados precisa coletar e quando deve
            encaminhar a conversa para uma pessoa. A demonstração ajuda sua equipe a avaliar as
            respostas antes de colocar o fluxo em operação.
          </p>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            <Aspect
              title="Conhecimento utilizado"
              text="Informações e documentos que você aprova: produtos, processos, políticas e perguntas frequentes."
            />
            <Aspect
              title="Ações autorizadas"
              text="O que a IA pode responder, quais dados deve coletar e quais rotinas pode registrar."
            />
            <Aspect
              title="Momento da transferência"
              text="Critérios definidos por você para encaminhar a conversa a uma pessoa, com o contexto do atendimento."
            />
          </div>
          <div className="mt-8">
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-primary/40 bg-primary/10 text-foreground hover:bg-primary/20"
            >
              <a href={IA_HREF} onClick={() => click("testar_ia_secao")}>
                Testar a IA do meu segmento
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* 8 — Integrações e WhatsApp Oficial */}
      <Light id="integracoes">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-3xl font-bold md:text-4xl" style={{ fontFamily: "var(--font-display)" }}>
            Conecte o atendimento aos sistemas da sua operação
          </h2>
          <p className="mt-4 max-w-3xl text-muted-foreground">
            A Kentro oferece recursos de API e webhooks para conectar processos de atendimento a
            outros sistemas. A disponibilidade de cada fluxo depende do conector, dos dados
            acessíveis e da configuração necessária. Na demonstração, avaliamos quais informações sua
            operação precisa consultar ou atualizar.
          </p>

          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <div className="glass-card rounded-2xl p-5">
              <span className="gradient-primary inline-flex size-10 items-center justify-center rounded-xl text-white shadow-glow">
                <ShieldCheck className="size-5" />
              </span>
              <h3 className="mt-4 text-lg font-semibold">WhatsApp API Oficial</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                O atendimento no WhatsApp usa a API Oficial e segue as regras da Meta. As condições
                de migração do número são avaliadas antes da ativação. Os custos de plataforma são
                separados dos custos de mensageria cobrados pela Meta. Não há promessa de imunidade
                a bloqueios nem de concessão automática de selo.
              </p>
            </div>
            <div className="glass-card rounded-2xl p-5">
              <span className="gradient-primary inline-flex size-10 items-center justify-center rounded-xl text-white shadow-glow">
                <Plug className="size-5" />
              </span>
              <h3 className="mt-4 text-lg font-semibold">Integrações e SMS</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Cada integração é avaliada por sistema, tipo de conexão, ações suportadas e
                requisitos técnicos — a matriz completa é publicada após validação. A Kentro não
                oferece disparo nativo de SMS; quando houver necessidade, a automação com um
                provedor externo deve ser avaliada por integração.
              </p>
              <Link
                to="/integracoes"
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-glow hover:underline"
                onClick={() => click("integracoes")}
              >
                Ver integrações <ChevronRight className="size-4" />
              </Link>
            </div>
          </div>
        </div>
      </Light>

      {/* 9 — Provas de uso */}
      <section id="clientes" className="py-16 md:py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold md:text-4xl" style={{ fontFamily: "var(--font-display)" }}>
            Veja como clientes usam a Kentro
          </h2>
          <p className="mt-4 max-w-3xl text-muted-foreground">
            Publicamos casos com cliente identificado, contexto do problema e recursos utilizados.
            Números só entram na página quando há período, base de comparação e forma de cálculo
            verificáveis. Nos vídeos, mantemos resumo textual e transcrição para que o conteúdo não
            dependa de assistir à gravação.
          </p>
          <div className="mt-8">
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/15 bg-white/5 text-foreground hover:bg-white/10"
            >
              <Link to="/cases" onClick={() => click("cases")}>
                Ver casos de clientes <ArrowRight />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* 10 — Implantação e proposta */}
      <Light>
        <div className="mx-auto max-w-5xl">
          <h2 className="text-3xl font-bold md:text-4xl" style={{ fontFamily: "var(--font-display)" }}>
            Como começar com a Kentro?
          </h2>
          <ol className="mt-8 grid gap-3 md:grid-cols-2">
            {STEPS.map((s, i) => (
              <li key={s} className="glass-card flex gap-3 rounded-xl p-4 text-sm">
                <span className="gradient-primary inline-flex size-7 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white">
                  {i + 1}
                </span>
                {s}
              </li>
            ))}
          </ol>
          <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <h3 className="text-lg font-semibold">Proposta e investimento</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Solicite uma proposta para a configuração da sua empresa. Informe quantas pessoas vão
              atender, quais canais serão utilizados e quais automações ou integrações são
              necessárias. A proposta detalha o que está incluído e os custos adicionais aplicáveis —
              implantação, consumo de mensageria e serviços de terceiros.
            </p>
          </div>
        </div>
      </Light>

      {/* 11 — FAQ */}
      <section id="duvidas" className="py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold md:text-4xl" style={{ fontFamily: "var(--font-display)" }}>
            Perguntas que ajudam a decidir
          </h2>
          <div className="mt-8 space-y-3">
            {CHATGPT_FAQS.map((f) => (
              <details
                key={f.q}
                className="glass-card group rounded-xl px-4 py-3 [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-3 text-left text-sm font-semibold md:text-base">
                  <h3 className="text-sm font-semibold md:text-base">{f.q}</h3>
                  <span className="text-primary-glow transition group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
          <Link
            to="/perguntas-frequentes"
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-primary-glow hover:underline"
            onClick={() => click("faq_completo")}
          >
            Ver todas as perguntas frequentes <ChevronRight className="size-4" />
          </Link>
        </div>
      </section>

      {/* 12 — Conversão final */}
      <section id="demonstracao" className="gradient-hero relative overflow-hidden py-16 md:py-20">
        <div className="circuit-pattern absolute inset-0 opacity-20" />
        <div className="relative mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <h2
              className="text-3xl font-bold md:text-4xl"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Veja a Kentro aplicada ao seu atendimento
            </h2>
            <p className="mt-4 text-muted-foreground">
              Conte como sua equipe atende hoje. Vamos apresentar um fluxo com os recursos
              relevantes para sua operação e esclarecer o que precisa ser configurado.
            </p>
            <p className="mt-4 text-sm text-muted-foreground">
              Usamos seus dados apenas para entrar em contato sobre esta solicitação de
              demonstração. Veja o{" "}
              <Link to="/aviso-de-privacidade" className="text-primary-glow hover:underline">
                aviso de privacidade
              </Link>
              .
            </p>
            <div className="mt-6">
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/15 bg-white/5 text-foreground hover:bg-white/10"
              >
                <a
                  href={WHATSAPP_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    click("whatsapp_final");
                    trackMetaPixel("Contact", { content_name: "whatsapp_chatgpt" });
                  }}
                >
                  Falar no WhatsApp
                </a>
              </Button>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Também atendemos por telefone: {KENTRO.phones[0].display}.
            </p>
          </div>

          <div className="glass-card rounded-2xl p-6 sm:p-8">
            <h3 className="text-lg font-semibold">Solicitar demonstração</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Nome, WhatsApp, empresa e e-mail. Detalhes da operação podem ser combinados no
              contato.
            </p>
            <div className="mt-5">
              <LeadForm source="chatgpt" submitLabel="Solicitar demonstração" />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function Light({ id, children }: { id?: string; children: React.ReactNode }) {
  return (
    <section id={id} className="border-y border-white/5 bg-background/40 py-16 md:py-20">
      <div className="px-4 sm:px-6 lg:px-8">{children}</div>
    </section>
  );
}

function Fact({ term, desc }: { term: string; desc: string }) {
  return (
    <div className="glass-card rounded-xl p-4">
      <dt className="text-xs font-semibold uppercase tracking-wider text-primary-glow">{term}</dt>
      <dd className="mt-1.5 text-sm text-muted-foreground">{desc}</dd>
    </div>
  );
}

function Aspect({ title, text }: { title: string; text: string }) {
  return (
    <div className="glass-card rounded-2xl p-5">
      <h3 className="text-base font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{text}</p>
    </div>
  );
}

function Bubble({ side, children }: { side: "user" | "ai"; children: React.ReactNode }) {
  const isAi = side === "ai";
  return (
    <div className={`flex ${isAi ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
          isAi
            ? "gradient-primary rounded-br-sm text-white"
            : "rounded-bl-sm border border-white/10 bg-white/5"
        }`}
      >
        <div
          className={`mb-0.5 text-[10px] uppercase tracking-wider ${
            isAi ? "text-white/70" : "text-muted-foreground"
          }`}
        >
          {isAi ? "Assistente Kentro" : "Cliente"}
        </div>
        {children}
      </div>
    </div>
  );
}
